import { useState, FC, useEffect } from 'react';
import { Button, Typography } from "@mui/material";
import { useTranslation } from 'react-i18next';
import useAuth from '@/components/Authentication/hooks/useAuth';
import { Tooltip } from '@gisce/oficina-virtual-components';
import { data_create_api_resource, data_update_api_resource } from '@/utils/http_functions';
import { setElectronicNotification } from '@/components/Contracts/actions/contracts';
import dayjs from 'dayjs';
import ErrorMessage from '@/components/Error/ErrorMessage';
import ContractElectronicNotificationDialogContent from '../../ModificationDialog/components/ElectronicNotification';
import SignatureProcess from '@/components/SignatureProcess';
import { IElecContract } from '@/components/Contracts/types/contracts';
import TextFieldReadOnly from '@/components/TextFieldReadOnly/TextFieldReadOnly';
import ElectronicNotificationStatus from './ElectronicNotificationStatus';
import axios from 'axios';
import { getIP } from '@/components/Contractation/utils/getIP';

interface ElectronicNotificationActionProps {
  contract: IElecContract;
  openDirectly?: boolean;
  actionOnClose?: () => void;
}

const getElecNotifStatus = (contract: IElecContract) => {
  if (contract.electronic_notification) {
    return 'active';
  } else if (contract.date_request_electronic_notification) {
    return 'requested';
  } else {
    return 'unactive';
  }
};

const ElectronicNotificationAction: FC<ElectronicNotificationActionProps> = ({ contract, openDirectly=false, actionOnClose }) => {
  const { t } = useTranslation();
  const { tokenVals, token } = useAuth();

  const [sending, setSending] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(openDirectly);
  const [error, setError] = useState<null | string | boolean>(null);
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState<'active' | 'requested' | 'unactive'>(getElecNotifStatus(contract));

  const handleDialogOpen = () => {
    setDialogOpen(true);
    setError(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    if (actionOnClose) {
      actionOnClose()
    }
  };

  useEffect(() => {
    setStatus(getElecNotifStatus(contract))
  }, [contract.electronic_notification, contract.date_request_electronic_notification]);

  const handleSubmit = async (email: string, mobile: string, scope: 'current' | 'all', signature: boolean) => {
    let requestError = false;
    let errorMessage = t('common:text.contractation_leads_generic_error');

    mobile = mobile.replace(/\D/g, '').slice(2);
    const all_contracts = scope === 'all';
    const ip = await getIP();

    setSending(true);
    try {
      const newData = { mobile, email };
      const electronicNotificationData = {all_contracts, signature, ip}
      await data_update_api_resource(token, 'users/', newData);
      await data_create_api_resource(token, `contracts/${contract.id}/electronic_notification/`, electronicNotificationData);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.errors?.ov_user?.[0]) {
        errorMessage = err.response.data.errors.ov_user[0];
      }
      requestError = true;
    } finally {
      if (!requestError) {
        setElectronicNotification({
          date_request_electronic_notification: dayjs()
        });
        setStatus('requested')
      }
      setError(requestError ? errorMessage : null);
      setSending(false);
      setDialogOpen(requestError);
      setSubmitted(true);
    }
  };

  const currentElecNotifStatus = getElecNotifStatus(contract);
  const CustomErrorMsg = () => <ErrorMessage><span>{typeof error === 'string' && error}</span></ErrorMessage>;

  const ElecNotifCases = {
    requested: { checked: false, label: t('common:text.electronic_notification_requested') },
    active: { checked: true, label: t('common:text.electronic_notification_active') },
    unactive: { checked: false, label: t('common:text.electronic_notification_disabled') }
  };

  return (
    <div>
      <ContractElectronicNotificationDialogContent
        open={dialogOpen}
        submitted={submitted}
        sending={sending}
        handleClose={handleDialogClose}
        handleSubmit={handleSubmit}
        status={currentElecNotifStatus}
      >
        {error ? <CustomErrorMsg /> : submitted && <SignatureProcess />}
      </ContractElectronicNotificationDialogContent>

      {!dialogOpen && (
        <div>
          <TextFieldReadOnly label={t('common:text.electronic_notification_title')}>
            <div style={{ display: 'flex'}}>
              <ElectronicNotificationStatus status={status} />
              {currentElecNotifStatus !== 'active' && (
                <Tooltip
                  title={tokenVals?.readonly && t("common:text.token_readonly_tooltip")}
                  placement="right"
                  arrow
                  disableFocusListener={!tokenVals?.readonly}
                  disableHoverListener={!tokenVals?.readonly}
                  disableTouchListener={!tokenVals?.readonly}
                  slotProps={{
                    popper: {
                      modifiers: [{ name: 'offset', options: { offset: [0, -5] } }]
                    }
                  }}
                >
                  <Button
                    color="primary"
                    variant="text"
                    disabled={
                      currentElecNotifStatus === "requested" ||
                      !!tokenVals?.readonly ||
                      submitted
                    }
                    title={t('common:text.electronic_notification_edit_data')}
                    onClick={handleDialogOpen}
                    style={{paddingTop: 8}}
                  >
                    {ElecNotifCases[currentElecNotifStatus as keyof typeof ElecNotifCases]?.label}
                  </Button>
                </Tooltip>
                )
              }
            </div>
          </TextFieldReadOnly>
        </div>
      )}

      {currentElecNotifStatus === 'requested' && (
        <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '12px' }}>
          {t('common:text.electronic_notification_process_already')}
        </Typography>
      )}
    </div>
  );
};

export default ElectronicNotificationAction;
